import { apiInstance, captureAdminApiError } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { getApiErrorMessage } from '@diamond/sol-admin/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const SUBMIT_ORDER_URL = 'backoffice/order/submit';
const VALIDATE_VOUCHERS_URL = 'backoffice/order';
const DEFAULT_SUBMIT_ORDER_ERROR_MESSAGE = 'Gagal Submit Order';

type SubmitOrderPayload = {
  id: string;
};

type SubmitOrderResponse = {
  success?: boolean;
  message?: string;
};

type ValidateVouchersResponse = {
  results?: Array<{
    code: string;
    eligible: boolean;
    reasons: string[];
    reward?: unknown;
  }>;
};

type LoadingStep = 'idle' | 'submitting' | 'validating';

const submitOrderFetcher = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<SubmitOrderResponse> => {
  const response = await apiInstance(token).patch<SubmitOrderResponse>(
    `${SUBMIT_ORDER_URL}/${payload.id}`,
    JSON.stringify({ id: payload.id })
  );
  return response.data;
};

const validateVouchersFetcher = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<ValidateVouchersResponse> => {
  const response = await apiInstance(token).post<ValidateVouchersResponse>(
    `${VALIDATE_VOUCHERS_URL}/${payload.id}/validate/vouchers`
  );
  return response.data;
};

export const useSubmitOrderWithValidation = () => {
  const session = useAuthStore();
  const token = session.access_token;
  const queryClient = useQueryClient();

  const submitOrderMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) =>
      submitOrderFetcher(token, payload),
  });

  const validateVouchersMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) =>
      validateVouchersFetcher(token, payload),
  });

  const submitOrderWithValidation = async (
    payload: SubmitOrderPayload
  ): Promise<void> => {
    try {
      await submitOrderMutation.mutateAsync(payload);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        DEFAULT_SUBMIT_ORDER_ERROR_MESSAGE,
        {
          includeStatusCode: true,
        }
      );

      captureAdminApiError({
        error,
        requestPath: `${SUBMIT_ORDER_URL}/${payload.id}`,
        httpMethod: 'PATCH',
        queryKey: 'submit-order',
        operation: 'submit_order',
        feature: 'activity',
        extras: { orderId: payload.id },
        user: {
          id: session.userId,
          email: session.userEmail,
        },
        level: 'error',
      });

      throw new Error(errorMessage);
    }

    try {
      await validateVouchersMutation.mutateAsync(payload);
    } catch (error: unknown) {
      captureAdminApiError({
        error,
        requestPath: `${VALIDATE_VOUCHERS_URL}/${payload.id}/validate/vouchers`,
        httpMethod: 'POST',
        queryKey: 'submit-order-validate-vouchers',
        operation: 'validate_vouchers',
        feature: 'activity',
        extras: { orderId: payload.id },
        user: {
          id: session.userId,
          email: session.userEmail,
        },
        level: 'error',
      });
    }
    queryClient.invalidateQueries({ queryKey: [`Activities-${payload.id}`] });
    queryClient.invalidateQueries({ queryKey: ['Activities'] });
  };

  let loadingStep: LoadingStep = 'idle';
  if (submitOrderMutation.isPending) {
    loadingStep = 'submitting';
  } else if (validateVouchersMutation.isPending) {
    loadingStep = 'validating';
  }

  const isLoading = loadingStep !== 'idle';

  return {
    submitOrderWithValidation,
    loadingStep,
    isLoading,
    submitOrderMutation,
    validateVouchersMutation,
  };
};

export type UseSubmitOrderWithValidationReturn = ReturnType<
  typeof useSubmitOrderWithValidation
>;
